import React, { useEffect, useState } from "react";
import i18n from "i18next";
import { Box, Typography } from "@mui/material";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { ConfiguratorTreeModelSource } from "src/api/generated/erp/configurator/configuratorTree/configuratorTreeModelSource.ts";
import { ConfiguratorInput } from "src/api/generated/io/aavo/applications/db/erp/types/configuratorInput.ts";
import { FormResult } from "src/components/common/forms/types.ts";
import { PartConfiguratorType } from "src/api/generated/erp/configurator/configuratorType/partConfiguratorType.ts";
import { PartConfiguratorFormApi } from "src/api/generated/erp/configurator/api/partConfiguratorFormApi.ts";
import { ConfigurationPropertyValues } from "src/api/generated/erp/configurator/model/configurationPropertyValues.ts";
import { AavoFormFooter } from "src/components/common/forms/AavoFormFooter.tsx";
import { AavoMosaic } from "src/components/common/mosaic/AavoMosaic.tsx";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { useMessageDialog } from "src/components/common/dialogs/messageDialog/MessageDialogContext.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { PropertyModelIssuesView } from "src/components/views/erp/configurator/configuratorForm/propertyModel/PropertyModelIssuesView.tsx";
import { DataDirtyStateChangeHandler } from "src/utils/dataDirtyStateChangeHandler.ts";
import { ResetConfiguratorValuesButton } from "src/components/views/erp/configurator/configuratorForm/components/ResetConfiguratorValuesButton.tsx";
import { defaultCheckpointState } from "src/components/views/erp/configurator/configuratorForm/checkpoints/configurationCheckpoints.ts";
import {
	PART_CONFIGURATION_NOT_SAVED,
	PartConfiguratorTreeResult,
} from "src/components/views/erp/configurator/configuratorForm/configuratorTree/PartConfiguratorTreeStatefulView.tsx";
import { PropertyModelConfiguratorTreeNav } from "src/components/views/erp/configurator/configuratorForm/propertyModel/PropertyModelConfiguratorTreeNav.tsx";
import { PropertyModelConfigurationNodeForm } from "src/components/views/erp/configurator/configuratorForm/propertyModel/PropertyModelConfigurationNodeForm.tsx";
import {
	EvaluatePartConfiguration,
	PartConfigurationSchema,
	PartConfigurationSchemaProperty,
} from "src/components/views/erp/configurator/configuratorForm/propertyModel/partConfigurationSdk.ts";
import {
	addChildNode,
	cloneChildNode,
	evaluatePropertyModelInput,
	getNode,
	getNodeInputValues,
	getNodeIssues,
	getNodeSchemaProperties,
	getRootNode,
	groupIssuesByNode,
	PropertyInputValues,
	PropertyModelNodePath,
	propertyModelNodePathKey,
	propertyModelTreeHasSubConfigurations,
	PropertyModelEdit,
	removeChildNode,
	resetInputsWithChangedDefaults,
	setNodeInputValue,
} from "src/components/views/erp/configurator/configuratorForm/propertyModel/propertyModelInputTree.ts";

export interface PropertyModelConfiguratorViewProps {
	configuratorType: PartConfiguratorType;
	/** The configuration schema of the target, whose rule engine is prepared. */
	schema: PartConfigurationSchema;
	evaluatePartConfiguration: EvaluatePartConfiguration;
	initialInputValues: PropertyInputValues;
	hasSavedConfiguration: boolean;
	reloadInitData: (source: ConfiguratorTreeModelSource) => Promise<unknown>;
	onSubmit?: (result: FormResult<ConfiguratorInput>) => Promise<unknown>;
	onCompleted?: (result: FormResult<PartConfiguratorTreeResult>) => Promise<unknown>;
	readonly?: boolean;
	hideFooter?: boolean;
	onDataDirtyStateChanged?: DataDirtyStateChangeHandler;
	/** Extra footer actions, e.g. a test printout, which get the values resolved from the current input. */
	propertyModelFooterExtraComponents?: (params: {
		resolvePropertyValues: () => Promise<ConfigurationPropertyValues>;
	}) => React.ReactNode;
}

/**
 * The generic configurator of a product family version that takes input by property name. It keeps the inputs that
 * the user has set for the whole tree and evaluates the schema's rules in the browser after every change, as Custom
 * Apps do; inputs that are not set get their defaults, so they follow the values that their defaults read. The server
 * validates and resolves the input again when it is saved.
 */
export function PropertyModelConfiguratorView({
	configuratorType,
	schema,
	evaluatePartConfiguration,
	initialInputValues,
	hasSavedConfiguration,
	reloadInitData,
	onSubmit,
	onCompleted,
	readonly = false,
	hideFooter,
	onDataDirtyStateChanged,
	propertyModelFooterExtraComponents,
}: PropertyModelConfiguratorViewProps) {
	const [state, setState] = useState(() =>
		evaluatePropertyModelInput(evaluatePartConfiguration, schema, initialInputValues),
	);
	const { values: inputValues, evaluation } = state;
	const [selectedPath, setSelectedPath] = useState<PropertyModelNodePath>([]);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
	const [configurationSessionId] = useState(() => crypto.randomUUID());

	const showMessageDialog = useMessageDialog();
	const { openDialog } = useGenericDialog();

	useEffect(() => {
		onDataDirtyStateChanged?.({
			isDirty: hasUnsavedChanges,
			confirmCloseWhileDirtyMessage: i18n.t("confirm_close_configurator_without_saving"),
		});
	}, [hasUnsavedChanges, onDataDirtyStateChanged]);

	const selectedNode = getNode(evaluation, selectedPath) ?? getRootNode(evaluation);
	const showTree = propertyModelTreeHasSubConfigurations(schema, evaluation, inputValues);

	return (
		<VerticalBox sx={{ flex: 1 }}>
			<AavoMosaic
				viewId={"B4D1A8E37C2F9A60"}
				layout={{
					type: "row",
					splitPercentage: 30,
					first: {
						type: "panel",
						hidden: !showTree,
						content: (
							<PropertyModelConfiguratorTreeNav
								schema={schema}
								evaluation={evaluation}
								rootInputValues={inputValues}
								selectedPath={selectedNode.path}
								readonly={readonly}
								selectNode={setSelectedPath}
								addChild={async (parentPath, property) => addChild(parentPath, property)}
								removeNode={removeNode}
								cloneNode={cloneNode}
							/>
						),
					},
					second: {
						type: "panel",
						content: (
							<PropertyModelConfigurationNodeForm
								key={propertyModelNodePathKey(selectedNode.path)}
								node={selectedNode}
								schemaProperties={getNodeSchemaProperties(schema, selectedNode)}
								issues={getNodeIssues(evaluation, selectedNode.path)}
								inputValues={getNodeInputValues(inputValues, selectedNode.path) ?? {}}
								readonly={readonly}
								onInputChanged={async (propertyName, value) =>
									changeInputValues(
										setNodeInputValue(inputValues, selectedNode.path, propertyName, value),
										{ path: selectedNode.path, propertyNames: [propertyName] },
									)
								}
								removeChildren={async (propertyName, indexes) =>
									removeChildren(selectedNode.path, propertyName, indexes)
								}
							/>
						),
					},
				}}
			/>
			{!hideFooter && (
				<AavoFormFooter sx={{ paddingLeft: 1 }}>
					{hasUnsavedChanges ?
						<Typography sx={{ color: "warning.main", alignSelf: "center", flex: 1, textAlign: "center" }}>
							{i18n.t("configurator_has_unsaved_changes_message")}
						</Typography>
					:	<Box sx={{ flex: 1 }} />}
					{propertyModelFooterExtraComponents?.({ resolvePropertyValues })}
					{!readonly && (
						<ResetConfiguratorValuesButton
							resetToExistingConfiguration={
								hasSavedConfiguration ? () => reloadInitData("SAVED_CONFIGURATION") : undefined
							}
							resetToDefaultValues={() => reloadInitData("SCRATCH")}
							color={"secondary"}
						/>
					)}
					<AsyncButton color={"secondary"} label={i18n.t("close")} icon={faTimes} onClick={onCancel} />
					{!readonly && <AsyncButton label={i18n.t("ok")} onClick={onSubmitForm} />}
				</AavoFormFooter>
			)}
		</VerticalBox>
	);

	/** Applies [edit], whose new input values are [newValues], and resets the inputs whose defaults it changed. */
	function changeInputValues(newValues: PropertyInputValues, edit: PropertyModelEdit) {
		const resetValues = resetInputsWithChangedDefaults(
			evaluatePartConfiguration,
			schema,
			evaluation,
			newValues,
			edit,
		);
		setState(evaluatePropertyModelInput(evaluatePartConfiguration, schema, resetValues));
		setHasUnsavedChanges(true);
	}

	function addChild(parentPath: PropertyModelNodePath, property: PartConfigurationSchemaProperty) {
		const { rootValues, childPath } = addChildNode({
			rootValues: inputValues,
			parentPath,
			propertyName: property.name,
			isList: property.datatype === "SUB_CONFIGURATION_LIST",
		});
		changeInputValues(rootValues, { path: parentPath, propertyNames: [property.name] });
		setSelectedPath(childPath);
	}

	async function removeNode(path: PropertyModelNodePath) {
		setSelectedPath(path.slice(0, -1));
		changeInputValues(removeChildNode(inputValues, path), {
			...subConfigurationEdit(path),
			removedChildIndexes: [path[path.length - 1]?.index ?? 0],
		});
	}

	function removeChildren(parentPath: PropertyModelNodePath, propertyName: string, indexes: number[]) {
		// Later list elements first, so that the positions of the others stay.
		const newValues = [...indexes]
			.sort((a, b) => b - a)
			.reduce((values, index) => removeChildNode(values, [...parentPath, { propertyName, index }]), inputValues);
		changeInputValues(newValues, { path: parentPath, propertyNames: [propertyName], removedChildIndexes: indexes });
	}

	async function cloneNode(path: PropertyModelNodePath) {
		const cloned = cloneChildNode(inputValues, path);
		if (cloned == null) return;
		changeInputValues(cloned.rootValues, subConfigurationEdit(path));
		setSelectedPath(cloned.childPath);
	}

	/** The edit of the sub-configuration property that holds the child at [childPath]. */
	function subConfigurationEdit(childPath: PropertyModelNodePath): PropertyModelEdit {
		const step = childPath[childPath.length - 1];
		return { path: childPath.slice(0, -1), propertyNames: step == null ? [] : [step.propertyName] };
	}

	async function onSubmitForm() {
		if (evaluation.issues.length > 0) {
			openDialog(({ closeDialog }) => ({
				title: i18n.t("configurator_form_errors_title"),
				size: "sm",
				content: (
					<PropertyModelIssuesView
						groups={groupIssuesByNode(schema, evaluation)}
						selectNode={async (path) => {
							setSelectedPath(path);
							await closeDialog();
						}}
					/>
				),
				agentContext: {
					key: defineAgentUiContextKey("erp.configurator.propertyModelFormErrors.dialog"),
					title: i18n.t("configurator_form_errors_title"),
					data: { issues: evaluation.issues },
				},
			}));
			return;
		}

		if (onCompleted == null) {
			// Only configuration templates use the component input, and they do not support property-model versions.
			showMessageDialog({
				title: i18n.t("error"),
				content: i18n.t("property_model_configuration_cannot_be_saved_here"),
			});
			return;
		}

		const completionResult = await onCompleted({
			type: "success",
			value: {
				propertyValues: undefined,
				propertyInputValues: inputValues,
				configuratorInput: { inputComponentValues: [] },
				configurationSessionId: configurationSessionId,
				checkpointState: defaultCheckpointState,
			},
		});
		if (completionResult !== PART_CONFIGURATION_NOT_SAVED) setHasUnsavedChanges(false);
	}

	async function resolvePropertyValues() {
		const { propertyValues } = await PartConfiguratorFormApi.resolvePropertyModelState({
			configurationSessionId,
			configuratorType,
			propertyInputValues: inputValues,
		});
		return propertyValues;
	}

	async function onCancel() {
		await onSubmit?.({ type: "cancel", isEdited: hasUnsavedChanges });
		await onCompleted?.({ type: "cancel", isEdited: hasUnsavedChanges });
	}
}
